import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Alert,
  ProgressBar,
} from "react-bootstrap";
import api from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PollOptionResult {
  option_id: string;
  option_text: string;
  image_url?: string;
  vote_count: number;
  percentage: number;
}

interface Poll {
  id: number;
  title: string;
  description?: string;
  status: string;
  allow_multiple: boolean;
  show_results_before_vote: boolean;
  closes_at?: string;
  created_at: string;
  total_votes: number;
  user_has_voted: boolean;
  user_votes?: string[];
  parent_poll_id?: number;
  trigger_option_ids?: string; // JSON string e.g. '["1_opt_1"]'
  options: Array<{ id: string; option_text: string; image_url?: string }>;
  results: PollOptionResult[];
}

// ─── Component ────────────────────────────────────────────────────────────────

const EmployeePolls: React.FC = () => {
  const { t, dir } = useLanguage();
  const [polls, setPolls] = useState<Poll[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<number, string[]>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [changingVote, setChangingVote] = useState<Record<number, boolean>>({});

  useEffect(() => {
    loadPolls();
  }, []);

  const loadPolls = async () => {
    try {
      const response = await api.get("/polls/active");
      const data: Poll[] = response.data.polls;
      setPolls(data);
      const initialCollapsed: Record<number, boolean> = {};
      data.forEach((p) => {
        if (p.user_has_voted) initialCollapsed[p.id] = true;
      });
      setCollapsed((prev) => ({ ...initialCollapsed, ...prev }));
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load polls");
    }
  };

  const handleVote = async (pollId: number) => {
    const optionIds = selectedOptions[pollId];
    if (!optionIds || optionIds.length === 0) {
      setError(t("pleaseSelectOption"));
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post(`/polls/${pollId}/vote`, { option_ids: optionIds });
      setSuccess(t("voteRecorded"));
      setSelectedOptions({ ...selectedOptions, [pollId]: [] });
      setChangingVote((prev) => ({ ...prev, [pollId]: false }));
      await loadPolls();
      setTimeout(() => {
        setCollapsed((prev) => ({ ...prev, [pollId]: true }));
      }, 1800);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to vote");
    } finally {
      setLoading(false);
    }
  };

  const handleOptionChange = (
    pollId: number,
    optionId: string,
    allowMultiple: boolean,
  ) => {
    if (allowMultiple) {
      const current = selectedOptions[pollId] || [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      setSelectedOptions({ ...selectedOptions, [pollId]: next });
    } else {
      setSelectedOptions({ ...selectedOptions, [pollId]: [optionId] });
    }
  };

  const handleChangeVote = (poll: Poll) => {
    // Pre-select their existing votes
    setSelectedOptions((prev) => ({
      ...prev,
      [poll.id]: poll.user_votes || [],
    }));
    setChangingVote((prev) => ({ ...prev, [poll.id]: true }));
    setCollapsed((prev) => ({ ...prev, [poll.id]: false }));
  };

  const handleCancelChangeVote = (pollId: number) => {
    setChangingVote((prev) => ({ ...prev, [pollId]: false }));
  };

  const toggleCollapsed = (pollId: number) =>
    setCollapsed((prev) => ({ ...prev, [pollId]: !prev[pollId] }));

  // ─── Derived helpers ──────────────────────────────────────────────────────

  // Phase-2 filtering is now done server-side in getMyPolls.
  // The backend only returns phase-2 polls the user has unlocked.
  // Here we just look up children by parent_poll_id.
  const getVisiblePhase2 = (parentPoll: Poll): Poll[] => {
    if (!parentPoll.user_has_voted) return [];
    return polls.filter((p) => p.parent_poll_id === parentPoll.id);
  };

  // ─── Sub-renderers ────────────────────────────────────────────────────────

  const renderVotingGrid = (poll: Poll) => {
    const selected = selectedOptions[poll.id] || [];
    return (
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}
      >
        {poll.options.map((option) => {
          const isSelected = selected.includes(option.id);
          return (
            <div
              key={option.id}
              onClick={() =>
                handleOptionChange(poll.id, option.id, poll.allow_multiple)
              }
              style={{
                border: isSelected
                  ? "2.5px solid #0d6efd"
                  : "2px solid #dee2e6",
                borderRadius: "10px",
                overflow: "hidden",
                cursor: "pointer",
                boxShadow: isSelected
                  ? "0 0 0 3px rgba(13,110,253,0.15)"
                  : "none",
                background: "#fff",
                position: "relative",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            >
              {isSelected && (
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 10,
                    background: "#0d6efd",
                    color: "#fff",
                    borderRadius: "50%",
                    width: "22px",
                    height: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                >
                  ✓
                </div>
              )}
              <div
                style={{
                  position: "relative",
                  paddingTop: "100%",
                  background: "#f0f2f5",
                }}
              >
                {option.image_url ? (
                  <img
                    src={option.image_url}
                    alt={option.option_text || t("yourChoice")}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#adb5bd",
                      fontSize: "32px",
                    }}
                  >
                    🗳️
                  </div>
                )}
              </div>
              {option.option_text && (
                <div
                  style={{
                    padding: "8px 10px",
                    fontSize: "13px",
                    fontWeight: isSelected ? "600" : "400",
                    color: isSelected ? "#0d6efd" : "#212529",
                    textAlign: "center",
                    lineHeight: "1.3",
                  }}
                >
                  {option.option_text}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderResultsGrid = (poll: Poll) => {
    const maxVotes = Math.max(...poll.results.map((r) => r.vote_count), 1);
    return (
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}
      >
        {poll.results.map((result) => {
          const isMyVote = poll.user_votes?.includes(result.option_id);
          const isWinner =
            result.vote_count === maxVotes && result.vote_count > 0;
          return (
            <div
              key={result.option_id}
              style={{
                border: isMyVote ? "2.5px solid #0d6efd" : "2px solid #dee2e6",
                borderRadius: "10px",
                overflow: "hidden",
                background: "#fff",
                position: "relative",
              }}
            >
              {isWinner && poll.total_votes > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    left: "8px",
                    zIndex: 10,
                    background: "#ffc107",
                    color: "#000",
                    borderRadius: "4px",
                    padding: "1px 6px",
                    fontSize: "11px",
                    fontWeight: "bold",
                  }}
                >
                  🏆 {t("leading")}
                </div>
              )}
              {isMyVote && (
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 10,
                    background: "#0d6efd",
                    color: "#fff",
                    borderRadius: "50%",
                    width: "22px",
                    height: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                >
                  ✓
                </div>
              )}
              <div
                style={{
                  position: "relative",
                  paddingTop: "100%",
                  background: "#f0f2f5",
                }}
              >
                {result.image_url ? (
                  <img
                    src={result.image_url}
                    alt={result.option_text || t("yourChoice")}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      opacity: poll.total_votes === 0 ? 0.7 : 1,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#adb5bd",
                      fontSize: "32px",
                    }}
                  >
                    🗳️
                  </div>
                )}
              </div>
              <div style={{ padding: "8px 10px" }}>
                {result.option_text && (
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: isMyVote ? "600" : "400",
                      color: isMyVote ? "#0d6efd" : "#212529",
                      textAlign: "center",
                      marginBottom: "6px",
                    }}
                  >
                    {result.option_text}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "11px",
                    color: "#6c757d",
                    marginBottom: "4px",
                  }}
                >
                  <span>
                    {result.vote_count} {t("votes")}
                  </span>
                  <span
                    style={{
                      fontWeight: "bold",
                      color: isMyVote ? "#0d6efd" : "#495057",
                    }}
                  >
                    {result.percentage.toFixed(1)}%
                  </span>
                </div>
                <ProgressBar
                  now={result.percentage}
                  variant={isMyVote ? "primary" : "secondary"}
                  style={{ height: "5px" }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCollapsedSummary = (poll: Poll) => {
    const myVotes = poll.results.filter((r) =>
      poll.user_votes?.includes(r.option_id),
    );
    const winner = poll.results.reduce(
      (best, r) => (r.vote_count > best.vote_count ? r : best),
      poll.results[0],
    );
    return (
      <div className="d-flex align-items-center gap-2 flex-wrap py-1">
        {myVotes.map((v) => (
          <div key={v.option_id} className="d-flex align-items-center gap-1">
            {v.image_url && (
              <img
                src={v.image_url}
                alt={v.option_text}
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "4px",
                  objectFit: "cover",
                  border: "2px solid #0d6efd",
                }}
              />
            )}
            <span
              style={{ fontSize: "13px", color: "#274066", fontWeight: 600 }}
            >
              {v.option_text || t("yourChoice")}
            </span>
          </div>
        ))}
        <span className="text-muted" style={{ fontSize: "12px" }}>
          ·
        </span>
        {winner && poll.total_votes > 0 && (
          <span style={{ fontSize: "12px", color: "#cbcdcf" }}>
            🏆 {winner.option_text || t("leading")} (
            {winner.percentage.toFixed(0)}%)
          </span>
        )}
        <span className="text-muted" style={{ fontSize: "12px" }}>
          ·
        </span>
        <span style={{ fontSize: "12px", color: "#d7dadd" }}>
          {poll.total_votes} {t("votes")}
        </span>
      </div>
    );
  };

  // ─── Single poll card ─────────────────────────────────────────────────────

  const PollCard = ({
    poll,
    isPhase2 = false,
  }: {
    poll: Poll;
    isPhase2?: boolean;
  }) => {
    const isCollapsed = collapsed[poll.id] ?? false;
    const isChangingVote = changingVote[poll.id] ?? false;
    const phase2Polls = getVisiblePhase2(poll);
    const canChangeVote =
      poll.user_has_voted &&
      poll.closes_at &&
      new Date(poll.closes_at) > new Date();
    const showVotingUI = !poll.user_has_voted || isChangingVote;

    return (
      <>
        <Card className={`shadow-sm${isPhase2 ? " border-info" : ""}`}>
          {isPhase2 && (
            <div
              style={{
                background: "#0dcaf022",
                borderBottom: "1px solid #0dcaf044",
                padding: "6px 16px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Badge bg="info" pill style={{ fontSize: "11px" }}>
                🔗 {t("phase2Label")}
              </Badge>
              <small className="text-muted">{t("phase2FollowUp")}</small>
            </div>
          )}

          {/* Header */}
          <Card.Header
            className={
              poll.user_has_voted
                ? "bg-success text-white"
                : isPhase2
                  ? "bg-info text-white"
                  : "bg-primary text-white"
            }
            onClick={
              poll.user_has_voted ? () => toggleCollapsed(poll.id) : undefined
            }
            style={
              poll.user_has_voted
                ? { cursor: "pointer", userSelect: "none" }
                : undefined
            }
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                {poll.title ? (
                  <h5 className="mb-0 text-truncate">{poll.title}</h5>
                ) : (
                  <h5
                    className="mb-0 opacity-75 fst-italic"
                    style={{ fontSize: "14px" }}
                  >
                    {t("polls")}
                  </h5>
                )}
              </div>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {poll.user_has_voted && (
                  <Badge bg="light" text="dark">
                    ✓ {t("voted")}
                  </Badge>
                )}
                {poll.user_has_voted && (
                  <span
                    style={{ fontSize: "16px", opacity: 0.85, lineHeight: 1 }}
                  >
                    {isCollapsed ? "▼" : "▲"}
                  </span>
                )}
              </div>
            </div>
            {poll.user_has_voted && isCollapsed
              ? renderCollapsedSummary(poll)
              : poll.description && (
                  <small className="d-block mt-1 opacity-90">
                    {poll.description}
                  </small>
                )}
          </Card.Header>

          {/* Body */}
          {!isCollapsed && (
            <Card.Body>
              {poll.closes_at && (
                <Alert variant="warning" className="small mb-3 py-2">
                  ⏰ {t("pollCloses")}:{" "}
                  {new Date(poll.closes_at).toLocaleString()}
                </Alert>
              )}
              {poll.allow_multiple && !poll.user_has_voted && (
                <Alert variant="info" className="small mb-3 py-2">
                  ℹ️ {t("multipleChoiceHint")}
                </Alert>
              )}

              {showVotingUI ? (
                <>
                  {isChangingVote && (
                    <Alert variant="warning" className="small mb-3 py-2">
                      ✏️ {t("changingVote")}
                    </Alert>
                  )}
                  <div className="mb-3">{renderVotingGrid(poll)}</div>
                  <div className="d-flex gap-2">
                    <Button
                      variant={isPhase2 ? "info" : "primary"}
                      className="flex-grow-1"
                      onClick={() => handleVote(poll.id)}
                      disabled={
                        loading ||
                        !selectedOptions[poll.id] ||
                        selectedOptions[poll.id].length === 0
                      }
                    >
                      {loading
                        ? t("submitting")
                        : isChangingVote
                          ? t("updateVote")
                          : t("submitVote")}
                    </Button>
                    {isChangingVote && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => handleCancelChangeVote(poll.id)}
                      >
                        {t("cancel")}
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <Alert variant="success" className="mb-3 text-center py-2">
                    <strong>✓ {t("thanksForVoting")}</strong>
                    {poll.total_votes > 0 && (
                      <div className="small mt-1 text-muted">
                        {poll.total_votes}{" "}
                        {poll.total_votes !== 1
                          ? t("employeesHaveVoted")
                          : t("employeeHasVoted")}
                      </div>
                    )}
                  </Alert>
                  {renderResultsGrid(poll)}
                  <div className="d-flex gap-2 mt-3">
                    {canChangeVote && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="flex-grow-1"
                        onClick={() => handleChangeVote(poll)}
                      >
                        ✏️ {t("changeVote")}
                      </Button>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className={canChangeVote ? "" : "w-100"}
                      onClick={() => toggleCollapsed(poll.id)}
                    >
                      ▲ {t("collapse")}
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          )}
        </Card>

        {/* Phase 2 follow-up polls — shown below parent after voting */}
        {phase2Polls.length > 0 && (
          <div
            className="mt-2 ps-3"
            style={{ borderLeft: "3px solid #0dcaf0" }}
          >
            <small
              className="text-muted d-block mb-2"
              style={{
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              🔗 {t("phase2FollowUp")}
            </small>
            {phase2Polls.map((p2) => (
              <PollCard key={p2.id} poll={p2} isPhase2 />
            ))}
          </div>
        )}
      </>
    );
  };

  // Only render top-level polls (not phase-2 children) at the root level
  // Phase-2 children are rendered inside their parent's PollCard
  const topLevelPolls = polls.filter((p) => !p.parent_poll_id);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Container fluid className="py-3 px-2 px-md-4" dir={dir}>
      <Row className="mb-3">
        <Col>
          <h3 className="mb-2">{t("polls")}</h3>
          <p className="text-muted small mb-3">
            {t("pollsManagementSubtitle")}
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Row className="justify-content-center">
        {topLevelPolls.length === 0 ? (
          <Col xs={12} md={8} lg={6}>
            <Alert variant="info">
              <h5>{t("noActivePollsTitle")}</h5>
              <p className="mb-0">{t("noActivePollsDesc")}</p>
            </Alert>
          </Col>
        ) : (
          topLevelPolls.map((poll) => (
            <Col key={poll.id} xs={12} md={10} lg={8} xl={6} className="mb-3">
              <PollCard poll={poll} />
            </Col>
          ))
        )}
      </Row>
    </Container>
  );
};

export default EmployeePolls;
